import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';
import { LinkButton, VideoPlayer } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { PreviewScreen } from 'src/apps/ScenarioGeneric/PreviewScreen';
import { PronunciationTokenUpsell } from 'src/apps/Monetisation/BlobExperienceUpsell';
import { FeatureUpsellModal } from 'src/apps/Monetisation/FeatureUpsellModal';
import { useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import { usePronunciationIntroVideo } from '../../hooks/usePronunciationIntroVideo';
import { PronunciationViewWrapper } from '../PronunciationViewWrapper';
import * as S from './styles';

const msgs = defineMessages({
  title: { id: 'learnq_lessons_preview_title_pronunciation' },
  descriptionFree: {
    id: 'learnq_lessons_preview_description_pronunciation_free',
    defaultMessage: 'You get one pronunciation lesson for free each day',
  },
  descriptionPro: { id: 'learnq_lessons_preview_description_pronunciation' },
  cta: { id: 'scenario_button_start_lesson' },
  notEnoughLearnables: { id: 'pronunciationBuddy_moreWordsNeeded_title' },
  notEnoughLearnablesCta: { id: 'scenario_button_label' },
});

export const PreviewScreenPronunciation: FC<{
  learnables: LearnablePronunciationData[];
  isLocked: boolean;
  onContinue: () => void;
}> = ({ learnables, isLocked, onContinue }) => {
  const intl = useIntl();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const { experienceType } = useExperienceContext();
  const { user } = useUserContext();
  // Until /me resolves we assume Pro: briefly telling a subscriber they are on a
  // free allowance is the worse way to lose that race.
  const isFreeUser = !!user && !user.isPro;
  const introVideoSrc = usePronunciationIntroVideo(learnables);

  if (learnables.length === 0) {
    return (
      <PronunciationViewWrapper>
        <S.NotEnoughLearnablesLayout>
          <S.NotEnoughLearnablesText data-testid="LearnablesIntro-learnMoreWords">
            {intl.formatMessage(msgs.notEnoughLearnables)}
          </S.NotEnoughLearnablesText>
          {
            /* istanbul ignore next: please test in WEBSITE-7219 */ experienceType && (
              <LinkButton
                buttonType="primary"
                buttonSize="large"
                wide
                href={ROUTES.scenarios.learn}
              >
                {intl.formatMessage(msgs.notEnoughLearnablesCta)}
              </LinkButton>
            )
          }
        </S.NotEnoughLearnablesLayout>
      </PronunciationViewWrapper>
    );
  }

  return (
    <PreviewScreen
      media={
        <S.VideoStage data-testid="pronunciation-intro-video">
          {introVideoSrc && (
            <VideoPlayer src={introVideoSrc} playOnKeyboardShortcut={false} autoPlay muted />
          )}
        </S.VideoStage>
      }
      title={intl.formatMessage(msgs.title)}
      description={intl.formatMessage(isFreeUser ? msgs.descriptionFree : msgs.descriptionPro)}
      buttonText={intl.formatMessage(msgs.cta)}
      hideButtonIcon
      onClick={() => {
        if (isLocked) {
          setShowUpsellModal(true);
          return;
        }

        onContinue();
      }}
    >
      <S.LearnablesList>
        {learnables.map(l => (
          <S.LearnablesListItem key={l.learnableIdKey}>
            <S.LearnableTarget dir="auto">{l.mainTargetValue}</S.LearnableTarget>
            <S.LearnableSource dir="auto">{l.sourceDefinitionValue}</S.LearnableSource>
          </S.LearnablesListItem>
        ))}
      </S.LearnablesList>
      {showUpsellModal && (
        <PronunciationTokenUpsell
          onContinue={() => setShowUpsellModal(false)}
          legacy={
            <FeatureUpsellModal
              tokenType="buddy_type_pronunciation"
              onClose={() => setShowUpsellModal(false)}
            />
          }
        />
      )}
    </PreviewScreen>
  );
};
